import { FC, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Icon,
  IconButton,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useSplunkLogger } from '@resolutionlife/ui';
import { BiArchive } from 'react-icons/bi';
import {
  FileBrowserDirectoryFragment,
  useArchiveDirectoryMutation,
} from '../../../graphql/generated';

type DirectoryArchiveButtonProps = {
  directory: FileBrowserDirectoryFragment;
};

export const DirectoryArchiveButton: FC<DirectoryArchiveButtonProps> = ({
  directory,
}) => {
  const cancelRef = useRef(null);
  const toast = useToast();
  const splunkLogger = useSplunkLogger();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [archiveDirectory, { loading }] = useArchiveDirectoryMutation({
    refetchQueries: ['GetDirectory'],
  });

  const handleArchive = async () => {
    try {
      const { data } = await archiveDirectory({
        variables: {
          cedant: directory.cedant,
          directoryId: directory.id,
        },
        refetchQueries: ['GetDirectory'],
      });

      if (data) {
        toast({
          title: `Archived "${directory.name}"`,
          status: 'success',
          position: 'bottom',
        });
      }
    } catch {
      toast({
        title: `Unable to archive "${directory.name}"`,
        description: 'Please try again',
        status: 'error',
        position: 'bottom',
      });
      splunkLogger.error('Directory archive failed', { eventCategory: 'file_operation_error', cedantId: directory.cedant, directoryId: directory.id, directoryName: directory.name });
    } finally {
      onClose();
    }
  };

  return (
    <>
      <IconButton
        onClick={onOpen}
        aria-label="Archive directory"
        colorScheme="red"
        icon={<Icon as={BiArchive} fontSize="16" />}
        variant="ghost"
        data-testid="directory-archive-button"
      />

      <AlertDialog
        isOpen={isOpen}
        isCentered={true}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent data-testid="directory-archive-dialog">
            <AlertDialogHeader>Archive "{directory.name}"</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure? You can't undo this action afterwards.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                data-testid="cancel-directory-archive-button"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                ml={3}
                colorScheme="red"
                onClick={handleArchive}
                isLoading={loading}
                data-testid="confirm-directory-archive-button"
              >
                Archive
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
